import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  Users, 
  FileText, 
  Building, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  Calendar,
  UserPlus,
  Settings,
  Shield,
  FolderOpen,
  ArrowRight,
  RefreshCw,
  Eye,
  Lock,
  Unlock
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

interface DashboardStats {
  users: {
    total: number;
    active: number;
    inactive: number;
    newThisMonth: number;
    byRole: {
      admin: number;
      normal: number;
      reviewer: number;
    };
  };
  documents: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    overdue: number;
    uploadedToday: number;
    uploadedThisWeek: number;
  };
  organizations: {
    total: number;
    withDomain: number;
  };
  compliance: {
    compliantUsers: number;
    nonCompliantUsers: number;
    complianceRate: number;
  };
  recentActivity: Array<{
    id: string;
    type: 'document_upload' | 'document_approved' | 'document_rejected' | 'user_created';
    description: string;
    timestamp: string;
    user: string;
  }>;
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('week'); // week, month, all
  const [isHoveringPassword, setIsHoveringPassword] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
  }, [dateRange]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      const now = new Date();
      const startOfCurrentMonth = startOfMonth(now);
      const weekAgo = subDays(now, 7);
      
      // Fetch users statistics
      const { data: users } = await supabase
        .from('users')
        .select('*')
        .eq('is_deleted', false);
      
      const { data: newUsersThisMonth } = await supabase
        .from('users')
        .select('*')
        .gte('created_at', startOfCurrentMonth.toISOString())
        .eq('is_deleted', false);
      
      // Fetch documents statistics
      const { data: documents } = await supabase
        .from('documents')
        .select('*, requirement:user_document_requirements!documents_requirement_id_fkey(due_date)')
        .eq('is_deleted', false);
      
      const { data: todayDocs } = await supabase
        .from('documents')
        .select('*')
        .gte('upload_date', new Date().toISOString().split('T')[0])
        .eq('is_deleted', false);
      
      const { data: weekDocs } = await supabase
        .from('documents')
        .select('*')
        .gte('upload_date', weekAgo.toISOString())
        .eq('is_deleted', false);
      
      // Fetch organizations
      const { data: organizations } = await supabase
        .from('organizations')
        .select('*');
      
      // Fetch compliance data
      const { data: requirements } = await supabase
        .from('user_document_requirements')
        .select(`
          *,
          user:users!user_document_requirements_user_id_fkey(role),
          documents:documents!documents_requirement_id_fkey(status)
        `)
        .eq('is_current', true);
      
      // Calculate compliance
      const userCompliance = new Map<string, boolean>();
      requirements?.forEach(req => {
        if (req.user.role === 'normal') {
          const userId = req.user_id;
          const isCompliant = req.status === 'fulfilled' || 
            (req.documents && req.documents.some((d: any) => d.status === 'approved'));
          
          if (!userCompliance.has(userId)) {
            userCompliance.set(userId, true);
          }
          if (!isCompliant) {
            userCompliance.set(userId, false);
          }
        }
      });
      
      const compliantUsers = Array.from(userCompliance.values()).filter(v => v).length;
      const totalNormalUsers = users?.filter(u => u.role === 'normal').length || 0;
      
      // Fetch recent activity
      const { data: recentDocuments } = await supabase
        .from('documents')
        .select(`
          *,
          requirement:user_document_requirements!documents_requirement_id_fkey(
            user:users!user_document_requirements_user_id_fkey(full_name, email),
            definition:document_definitions(name)
          )
        `)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(10);
      
      const recentActivity = recentDocuments?.map(doc => ({
        id: doc.id,
        type: doc.status === 'pending_approval' ? 'document_upload' as const : 
              doc.status === 'approved' ? 'document_approved' as const : 
              'document_rejected' as const,
        description: `${doc.requirement.definition.name}`,
        timestamp: doc.created_at,
        user: doc.requirement.user.full_name || doc.requirement.user.email
      })) || [];
      
      // Calculate overdue documents
      const overdueCount = documents?.filter(doc => {
        const dueDate = doc.requirement?.due_date;
        return doc.status === 'pending_approval' && dueDate && new Date(dueDate) < now;
      }).length || 0;
      
      const dashboardStats: DashboardStats = {
        users: {
          total: users?.length || 0,
          active: users?.filter(u => u.is_active).length || 0,
          inactive: users?.filter(u => !u.is_active).length || 0,
          newThisMonth: newUsersThisMonth?.length || 0,
          byRole: {
            admin: users?.filter(u => u.role === 'admin').length || 0,
            normal: users?.filter(u => u.role === 'normal').length || 0,
            reviewer: users?.filter(u => u.role === 'reviewer').length || 0
          }
        },
        documents: {
          total: documents?.length || 0,
          pending: documents?.filter(d => d.status === 'pending_approval').length || 0,
          approved: documents?.filter(d => d.status === 'approved').length || 0,
          rejected: documents?.filter(d => d.status === 'rejected').length || 0,
          overdue: overdueCount,
          uploadedToday: todayDocs?.length || 0,
          uploadedThisWeek: weekDocs?.length || 0
        },
        organizations: {
          total: organizations?.length || 0,
          withDomain: organizations?.filter(o => o.domain).length || 0
        },
        compliance: {
          compliantUsers,
          nonCompliantUsers: totalNormalUsers - compliantUsers,
          complianceRate: totalNormalUsers > 0 ? (compliantUsers / totalNormalUsers) * 100 : 0
        },
        recentActivity
      };
      
      setStats(dashboardStats);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardStats();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'document_upload':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'document_approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'document_rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'user_created':
        return <UserPlus className="w-4 h-4 text-blue-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityText = (type: string) => {
    switch (type) {
      case 'document_upload':
        return 'Documento subido';
      case 'document_approved':
        return 'Documento aprobado';
      case 'document_rejected':
        return 'Documento rechazado';
      case 'user_created':
        return 'Usuario creado';
      default:
        return 'Actividad';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Panel de Administración</h1>
              <p className="mt-1 text-sm text-gray-600">
                Bienvenido al centro de control de REPSE Platform
              </p>
            </div>
            <div className="flex items-center gap-2 mt-4 sm:mt-0">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
              <div className="relative">
                <button
                  onClick={() => navigate('/profile')}
                  onMouseEnter={() => setIsHoveringPassword(true)}
                  onMouseLeave={() => setIsHoveringPassword(false)}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-all duration-200"
                >
                  {isHoveringPassword ? (
                    <Unlock className="w-4 h-4" />
                  ) : (
                    <Lock className="w-4 h-4" />
                  )}
                </button>
                {isHoveringPassword && (
                  <div className="absolute top-full mt-2 right-0 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                    Cambiar Contraseña
                    <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-800 transform rotate-45"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <span className="font-medium text-gray-900">Gestionar Usuarios</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
            </div>
          </button>

          <button
            onClick={() => navigate('/review')}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FileText className="w-5 h-5 text-yellow-600" />
                </div>
                <span className="font-medium text-gray-900">Revisar Documentos</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/definitions')}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FolderOpen className="w-5 h-5 text-green-600" />
                </div>
                <span className="font-medium text-gray-900">Definiciones</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
            </div>
          </button>

        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Users Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">USUARIOS</span>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats?.users.total}</p>
                <p className="text-sm text-gray-600">Total de usuarios</p>
              </div>
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Activos</span>
                  <span className="font-medium text-green-600">{stats?.users.active}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Nuevos este mes</span>
                  <span className="font-medium text-blue-600">+{stats?.users.newThisMonth}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Documents Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">DOCUMENTOS</span>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats?.documents.total}</p>
                <p className="text-sm text-gray-600">Total de documentos</p>
              </div>
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Pendientes</span>
                  <span className="font-medium text-yellow-600">{stats?.documents.pending}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Vencidos</span>
                  <span className="font-medium text-red-600">{stats?.documents.overdue}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">CUMPLIMIENTO</span>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {stats?.compliance.complianceRate.toFixed(1)}%
                </p>
                <p className="text-sm text-gray-600">Tasa de cumplimiento</p>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <div className="relative pt-1">
                  <div className="overflow-hidden h-2 mb-2 text-xs flex rounded bg-gray-200">
                    <div 
                      style={{ width: `${stats?.compliance.complianceRate}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500 transition-all duration-300"
                    />
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{stats?.compliance.compliantUsers} completos</span>
                    <span className="text-gray-600">{stats?.compliance.nonCompliantUsers} pendientes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Activity className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">ACTIVIDAD</span>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats?.documents.uploadedToday}</p>
                <p className="text-sm text-gray-600">Documentos hoy</p>
              </div>
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Esta semana</span>
                  <span className="font-medium">{stats?.documents.uploadedThisWeek}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Aprobados</span>
                  <span className="font-medium text-green-600">{stats?.documents.approved}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and Activity Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Distribution */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-gray-600" />
              Distribución de Usuarios
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Usuarios Normales</span>
                  <span className="text-sm font-medium">{stats?.users.byRole.normal}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(stats?.users.byRole.normal || 0) / (stats?.users.total || 1) * 100}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Revisores</span>
                  <span className="text-sm font-medium">{stats?.users.byRole.reviewer}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(stats?.users.byRole.reviewer || 0) / (stats?.users.total || 1) * 100}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Administradores</span>
                  <span className="text-sm font-medium">{stats?.users.byRole.admin}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(stats?.users.byRole.admin || 0) / (stats?.users.total || 1) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-gray-600" />
                Actividad Reciente
              </span>
              <button
                onClick={() => navigate('/review')}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Ver todo
                <ArrowRight className="w-4 h-4" />
              </button>
            </h3>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {stats?.recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500">
                      {activity.user} • {getActivityText(activity.type)}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {format(new Date(activity.timestamp), 'HH:mm', { locale: es })}
                  </span>
                </div>
              ))}
              {stats?.recentActivity.length === 0 && (
                <p className="text-center text-gray-500 py-8">No hay actividad reciente</p>
              )}
            </div>
          </div>
        </div>

        {/* Alerts Section */}
        {(stats?.documents.overdue || 0) > 0 && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-red-800">Documentos Vencidos</h4>
                <p className="text-sm text-red-700 mt-1">
                  Hay {stats?.documents.overdue} documento(s) vencidos que requieren atención inmediata.
                </p>
              </div>
              <button
                onClick={() => navigate('/review')}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Revisar ahora
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
